import { useTranslation, Trans } from 'react-i18next'
import styled from 'styled-components'
import { Button } from '@/components/button'
import CloseButton from '@/components/close-button'
import Icon from '@/components/icon'
import { createModal, useModalize } from '@/components/modalize/mui'
import useRole from '@/hooks/use-role'
import { breakpoints, colors } from '@/styles'
import { H4, ParagraphRegular } from '@/components/text'
import config from '@/config'

const { WEB_URL } = config

const Link = styled.a`
  display: inline;
  color: inherit;
  text-decoration: underline;
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 95vh;
  text-align: center;
`

const ScrollWrapper = styled.div`
  height: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 50px 40px 40px;

  svg {
    flex-shrink: 0;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    padding: 60px 16px 40px;
  }
`

const ModalTitle = styled(H4)`
  margin: 24px 0 12px;
`

const ModalText = styled(ParagraphRegular)`
  color: ${colors.grey56};
`

const StyledButton = styled(Button)`
  margin-top: 24px;
`

const ProcessingFeesModal = () => {
  const { t } = useTranslation()
  const { close } = useModalize()
  const isTenant = useRole() === 'tenant'

  const titleText = isTenant
    ? t('r.payment.view.processing_fees.title', 'Processing Fees')
    : t('l.payment.view.transaction_fees.title', 'Payout Fees')

  const messageText = isTenant ? (
    <Trans i18nKey="r.payment.view.processing_fees_modal.text">
      This processing fee is charged to facilitate your payment through liv.rent and our payment
      partners. Check our FAQ for the latest fee amounts as this may be subject to change without
      notice.
    </Trans>
  ) : (
    <Trans i18nKey="l.payment.view.processing_fees_modal.text">
      Based on the payment method, the fees charged for each payout may differ. Check our{' '}
      <Link href={`${WEB_URL}/faq`} target="_blank" rel="noopener noreferrer">
        FAQ
      </Link>{' '}
      for the latest fee amounts as this may be subject to change without notice.
    </Trans>
  )

  const buttonText = t('r.payment.view.ok.button', 'OK')

  return (
    <Wrapper
      role="dialog"
      aria-labelledby="fees-dialog-title"
      aria-describedby="fees-dialog-message"
    >
      <CloseButton onClick={close} />
      <ScrollWrapper>
        <Icon id="info_circle" width={70} height={70} />
        <ModalTitle id="fees-dialog-title">{titleText}</ModalTitle>
        <ModalText id="fees-dialog-message">{messageText}</ModalText>
        <StyledButton theme="primary" onClick={close}>
          {buttonText}
        </StyledButton>
      </ScrollWrapper>
    </Wrapper>
  )
}

export const show = createModal(ProcessingFeesModal, {
  width: '480px'
})
